import { phoneFormatConfig } from "./phoneMasks";

export const phoneConfig = Object.fromEntries(
  Object.entries(phoneFormatConfig).map(([code, cfg]) => [
    code,
    { ddi: cfg.ddi, localLength: cfg.formats.map((f) => f.localLength) },
  ])
);

export const getCountryCode = (countryCode) => {
  return phoneConfig[countryCode]?.ddi || "";
};

export const getPhoneValidation = (countryCode) => {
  const config = phoneConfig[countryCode];
  if (!config) return /^\d{8,15}$/;

  const { ddi, localLength } = config;

  if (localLength.length === 1) {
    return new RegExp(`^${ddi}\\d{${localLength[0]}}$`);
  } else {
    const min = Math.min(...localLength);
    const max = Math.max(...localLength);
    return new RegExp(`^${ddi}\\d{${min},${max}}$`);
  }
};

export const getPhoneMaxLength = (countryCode) => {
  const config = phoneConfig[countryCode];
  if (!config) return 15;

  const { ddi, localLength } = config;
  const maxLocal = Math.max(...localLength);
  return ddi.length + maxLocal;
};

export const validatePhone = (phone, countryCode) => {
  const regex = getPhoneValidation(countryCode);
  const cleanPhone = String(phone).replace(/\D/g, "");
  return regex.test(cleanPhone);
};

export const getLocalBounds = (countryCode) => {
  const cfg = phoneConfig[countryCode];
  if (!cfg) return { minLocal: 4, maxLocal: 20, ddi: "" };
  const minLocal = Math.min(...cfg.localLength);
  const maxLocal = Math.max(...cfg.localLength);
  return { minLocal, maxLocal, ddi: cfg.ddi };
};

export const isPhoneComplete = (value, countryCode) => {
  const digits = String(value || "").replace(/\D/g, "");
  const { minLocal, maxLocal, ddi } = getLocalBounds(countryCode);
  if (!digits || !ddi) return false;
  if (!digits.startsWith(ddi)) return false;
  const local = digits.slice(ddi.length);
  if (local.length < minLocal || local.length > maxLocal) return false;
  return getPhoneValidation(countryCode).test(digits);
};

export const getLocalPhoneValidation = (countryCode) => {
  const config = phoneConfig[countryCode];
  if (!config) return /^\d{8,15}$/;

  const { localLength } = config;
  if (localLength.length === 1) {
    return new RegExp(`^\\d{${localLength[0]}}$`);
  }
  const min = Math.min(...localLength);
  const max = Math.max(...localLength);
  return new RegExp(`^\\d{${min},${max}}$`);
};

export const isLocalPhoneComplete = (value, countryCode, ddiCode = null) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return false;
  const cfg = phoneConfig[countryCode];
  if (!cfg) {
    // Sem config de formato não há comprimento esperado para comparar, então
    // aqui ainda vale detectar DDI digitado junto: só quando, ao removê-lo,
    // sobra um número local plausível.
    const ddi = ddiCode ? String(ddiCode).replace(/\D/g, "") : null;
    if (ddi && digits.startsWith(ddi) && digits.length - ddi.length >= 7) {
      return false;
    }
    return digits.length >= 4;
  }
  // Com config, o próprio comprimento já rejeita DDI digitado junto (não há
  // país onde ddi.length + minLocal caiba em maxLocal). Comparar o começo do
  // número com o DDI invalidaria locais legítimos — ex.: DDD 55 no Brasil,
  // celulares 39X na Itália, 5XXXXXXX em Cuba.
  const { minLocal, maxLocal } = getLocalBounds(countryCode);
  if (digits.length < minLocal || digits.length > maxLocal) return false;
  return getLocalPhoneValidation(countryCode).test(digits);
};
